import {
  ApiBlueprint,
  createApiRef,
  discoveryApiRef,
  fetchApiRef,
  type DiscoveryApi,
  type FetchApi,
} from '@backstage/frontend-plugin-api';

/**
 * Client for the `migration` backend plugin, which serves the GitHub Migration
 * screens. This is the first API ref in this app — see
 * `plugins/migration-backend` for the routes and where their data comes from.
 */

/** Durable migration run with optional Argo display enrichment. */
export interface LiveRun {
  name: string;
  selectedServices?: string[];
  dryRun?: boolean;
  namespace: string;
  /** step1 | step2 | step3 | rollback | onboard-team */
  step: string;
  /** Durable database run status. */
  phase: string;
  /** Argo's status.progress, e.g. "3/7". */
  progress?: string;
  startedAt?: string;
  finishedAt?: string;
  url?: string;
  service?: string;
}

export interface TeamRow {
  name: string;
  entityRef?: string;
  onboarded: boolean;
  status: string;
  batch?: string;
  batches?: string[];
  updatedAt?: string;
  serviceCount: number;
  statusCounts: Record<string, number>;
  liveRuns: LiveRun[];
}

export interface ServiceRow {
  name: string;
  team: string;
  type?: string;
  batch?: string;
  entityRef?: string;
  clusters: string[];
  cloud?: string;
  appId?: string;
  jenkins?: string;
  status: string;
  updatedAt?: string;
  workflowUrl?: string;
  verificationEligible?: boolean;
  liveRuns: LiveRun[];
}

export interface TeamsResponse {
  teams: TeamRow[];
  /** False when the backend could not reach Argo — durable database status remains available. */
  liveStatusAvailable: boolean;
}

export interface ServicesResponse {
  team: string;
  services: ServiceRow[];
  /** Database run history with explicit selected service membership. */
  teamRuns: LiveRun[];
  liveStatusAvailable: boolean;
  statusFilter?: string;
  serviceType?: ServiceTypeFilter;
}

export interface MigrationApi {
  listTeams(batch?: string): Promise<TeamsResponse>;
  /** Optional single status or comma-separated exact statuses, matched with OR. */
  listServices(
    team: string,
    status?: string,
    serviceType?: ServiceTypeFilter,
  ): Promise<ServicesResponse>;
}

export type ServiceTypeFilter = 'lambda' | 'non-lambda';

export const migrationApiRef = createApiRef<MigrationApi>({
  id: 'plugin.migration.service',
});

export class MigrationClient implements MigrationApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  private async request<T>(path: string): Promise<T> {
    const base = await this.discoveryApi.getBaseUrl('migration');
    const res = await this.fetchApi.fetch(`${base}${path}`);
    if (!res.ok) {
      // Surface the backend's own message — it distinguishes "you are not a
      // devops-admin" from "that team does not exist", and hiding that behind a
      // generic error is how a permissions problem gets mistaken for a bug.
      let detail = res.statusText;
      try {
        const body = (await res.json()) as { error?: { message?: string } };
        detail = body?.error?.message ?? detail;
      } catch {
        /* response was not JSON; keep the status text */
      }
      throw new Error(detail);
    }
    return (await res.json()) as T;
  }

  listTeams(batch?: string): Promise<TeamsResponse> {
    const query =
      batch === undefined ? '' : `?batch=${encodeURIComponent(batch)}`;
    return this.request<TeamsResponse>(`/teams${query}`);
  }

  listServices(
    team: string,
    status?: string,
    serviceType?: ServiceTypeFilter,
  ): Promise<ServicesResponse> {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (serviceType) params.set('serviceType', serviceType);
    const query = params.size ? `?${params.toString()}` : '';
    return this.request<ServicesResponse>(
      `/teams/${encodeURIComponent(team)}/services${query}`,
    );
  }
}

export const migrationApi = ApiBlueprint.make({
  name: 'migration',
  // Params must be in CALLBACK form. Passing a plain object typechecks but
  // fails at runtime with EXTENSION_FACTORY_ERROR when the app starts.
  params: defineParams =>
    defineParams({
      api: migrationApiRef,
      deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
      factory: ({ discoveryApi, fetchApi }) =>
        new MigrationClient(discoveryApi, fetchApi),
    }),
});
